import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Progress } from './ui/progress';
import { Radio, Activity, Clock, AlertCircle, PlusCircle } from 'lucide-react';
import { rmsDevices as initialRmsDevices, sites } from '../mock';

const RMSDevices = () => {
  const navigate = useNavigate();
  const [rmsDevices, setRmsDevices] = useState(initialRmsDevices);
  const [newDevice, setNewDevice] = useState({
    deviceId: '',
    siteId: '',
    status: 'online',
    lastData: '1 min ago',
    dataPoints: 1440,
    syncErrors: 0,
  });

  const handleAddDevice = () => {
    if (newDevice.deviceId && newDevice.siteId) {
      setRmsDevices([newDevice, ...rmsDevices]);
      setNewDevice({
        deviceId: '',
        siteId: '',
        status: 'online',
        lastData: '1 min ago',
        dataPoints: 1440,
        syncErrors: 0,
      });
    }
  };

  const getStatusColor = (status) => {
    if (status === 'online') return '#10ac84';
    if (status === 'warning') return '#f39c12';
    return '#e74c3c';
  };

  const getStatusBg = (status) => {
    if (status === 'online') return '#e8f5f1';
    if (status === 'warning') return '#fff4e6';
    return '#fee';
  };

  const onlineDevices = rmsDevices.filter(d => d.status === 'online').length;
  const offlineDevices = rmsDevices.filter(d => d.status === 'offline').length;
  const warningDevices = rmsDevices.filter(d => d.status === 'warning').length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>Devices & Status</h1>
          <p className="text-base mt-2" style={{ color: '#576574' }}>Real-time monitoring of all RMS devices</p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button>
              <PlusCircle className="mr-2 h-4 w-4" /> Add Device
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Add New RMS Device</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="deviceId" className="text-right">
                  Device ID
                </Label>
                <Input
                  id="deviceId"
                  value={newDevice.deviceId}
                  onChange={(e) => setNewDevice({ ...newDevice, deviceId: e.target.value })}
                  className="col-span-3"
                  placeholder="e.g., RMS-PTN-006"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="siteId" className="text-right">
                  Site
                </Label>
                <Select
                  onValueChange={(value) => setNewDevice({ ...newDevice, siteId: value })}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select a site" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map(site => (
                      <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="status" className="text-right">
                  Status
                </Label>
                <Select
                  defaultValue="online"
                  onValueChange={(value) => setNewDevice({ ...newDevice, status: value })}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" onClick={handleAddDevice}>
                  Add Device
                </Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>


      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e3f2fd' }}>
                <Radio className="w-6 h-6" style={{ color: '#3498db' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{rmsDevices.length}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Total Devices</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e8f5f1' }}>
                <Activity className="w-6 h-6" style={{ color: '#10ac84' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#10ac84' }}>{onlineDevices}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Online</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#fff4e6' }}>
                <AlertCircle className="w-6 h-6" style={{ color: '#f39c12' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#f39c12' }}>{warningDevices}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Warning</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#fee' }}>
                <AlertCircle className="w-6 h-6" style={{ color: '#e74c3c' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#e74c3c' }}>{offlineDevices}</div>
            <div className="text-sm" style={{ color: '#576574' }}>Offline</div>
          </CardContent>
        </Card>
      </div>

      {/* Device Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Device Connectivity Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold" style={{ color: '#576574' }}>Online Devices</span>
                <span className="text-sm font-semibold" style={{ color: '#10ac84' }}>{onlineDevices}/{rmsDevices.length}</span>
              </div>
              <Progress value={(onlineDevices / rmsDevices.length) * 100} className="h-3" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold" style={{ color: '#576574' }}>Success Rate</span>
                <span className="text-sm font-semibold" style={{ color: '#10ac84' }}>{((onlineDevices / rmsDevices.length) * 100).toFixed(1)}%</span>
              </div>
              <Progress value={(onlineDevices / rmsDevices.length) * 100} className="h-3" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Device List */}
      <Card>
        <CardHeader>
          <CardTitle>RMS Device Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {rmsDevices.map((device) => {
              const site = sites.find(s => s.id === device.siteId);
              const statusColor = getStatusColor(device.status);
              const statusBg = getStatusBg(device.status);
              const successRate = ((device.dataPoints / 1440) * 100).toFixed(1);
              
              return (
                <div key={device.deviceId} onClick={() => navigate(`/site/${device.siteId}`)} className="p-6 rounded-lg border hover:shadow-md transition-shadow cursor-pointer">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: statusBg }}>
                        <Radio className="w-6 h-6" style={{ color: statusColor }} />
                      </div>
                      <div>
                        <div className="font-mono text-lg font-bold" style={{ color: '#0a3d62' }}>{device.deviceId}</div>
                        <div className="text-sm" style={{ color: '#576574' }}>Site: {device.siteId} - {site?.name}</div>
                      </div>
                    </div>
                    <Badge variant="outline" style={{ backgroundColor: statusBg, color: statusColor, borderColor: statusColor }}>
                      {device.status.toUpperCase()}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4" style={{ color: '#576574' }} />
                        <span className="text-xs" style={{ color: '#576574' }}>Last Data</span>
                      </div>
                      <div className="font-semibold" style={{ color: statusColor }}>{device.lastData}</div>
                    </div>
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                      <div className="text-xs mb-2" style={{ color: '#576574' }}>Data Points (24h)</div>
                      <div className="font-semibold text-lg" style={{ color: '#0a3d62' }}>{device.dataPoints}</div>
                      <div className="text-xs" style={{ color: '#576574' }}>of 1440</div>
                    </div>
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                      <div className="text-xs mb-2" style={{ color: '#576574' }}>Sync Errors</div>
                      <div className="font-semibold text-lg" style={{ color: device.syncErrors > 5 ? '#e74c3c' : '#10ac84' }}>{device.syncErrors}</div>
                    </div>
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                      <div className="text-xs mb-2" style={{ color: '#576574' }}>Success Rate</div>
                      <div className="font-semibold text-lg" style={{ color: parseFloat(successRate) > 95 ? '#10ac84' : '#f39c12' }}>{successRate}%</div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="text-xs mb-2" style={{ color: '#576574' }}>Data Transmission Quality</div>
                    <Progress value={parseFloat(successRate)} className="h-2" />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RMSDevices;
